import { IAreaTrendItem } from '../../customer-shared/models/IGlimpseNewReport';
import { IConsolidation } from '../../customer-shared/models/IGlimpseReport';
import { ReportPeriod } from '../../customer-shared/models/ReportPeriod';
import { ITrendData } from '../models/ITrendData';

export function reportLength(auditedMinutes: number) {
  return Math.round(auditedMinutes / 60);
}

export interface IConsolidationItemComparison {
  direction: 'up' | 'down' | 'nochange';
  evaluation: 'good' | 'bad' | 'nochange';
  val: string;
}

export interface IConsolidationStateComparison {
  qty: IConsolidationItemComparison | null;
  val: IConsolidationItemComparison | null;
  pct: IConsolidationItemComparison | null;
}

export interface IConsolidationComparison {
  c: IConsolidationStateComparison;
  nc: IConsolidationStateComparison;
  nm: IConsolidationStateComparison;
}

export function compareConsolidation(current: IConsolidation, prev?: IConsolidation): IConsolidationComparison | null {
  if (!prev) {
    return null;
  }

  function numFormatter(n: number): string {
    return String(Math.abs(n));
  }

  function cmpMap(a: number, b: number, gt: any, lt: any, eq: any) {
    if (a > b) {
      return gt;
    }
    if (a < b) {
      return lt;
    }
    return eq;
  }
  function cmp(
    a: number,
    b: number,
    inverse = false,
    formatter: (n: number) => string = numFormatter,
  ): IConsolidationItemComparison | null {
    return {
      direction: cmpMap(a, b, 'up', 'down', 'nochange'),
      evaluation: inverse ? cmpMap(a, b, 'bad', 'good', a == 0 ? 'good' : 'bad') : cmpMap(a, b, 'good', 'bad', 'good'),
      val: formatter(a - b),
    };
  }

  function currencyFormatter(n: number): string {
    return Math.abs(n).toFixed(1);
  }

  function percentageFormatter(n: number): string {
    return `${Math.abs(Math.round(n * 1000) / 10)}%`;
  }

  return {
    c: {
      pct: cmp(current.c.pct, prev.c.pct, false, percentageFormatter),
      qty: cmp(current.c.qty, prev.c.qty, false),
      val: cmp(current.c.val, prev.c.val, false, currencyFormatter),
    },
    nc: {
      pct: cmp(current.nc.pct, prev.nc.pct, true, percentageFormatter),
      qty: cmp(current.nc.qty, prev.nc.qty, true),
      val: cmp(current.nc.val, prev.nc.val, true, currencyFormatter),
    },
    nm: {
      pct: cmp(current.nm.pct, prev.nm.pct, true, percentageFormatter),
      qty: cmp(current.nm.qty, prev.nm.qty, true),
      val: cmp(current.nm.val, prev.nm.val, true, currencyFormatter),
    },
  };
}

export function getTrendData(trend: IAreaTrendItem[]): ITrendData {
  const pct = (num: number) => Math.round(num * 1000) / 10;

  return {
    Score: [
      {
        data: trend.map(item => ({
          t: item.date,
          y: item.score,
          areaId: item.areaId,
          periodId: item.periodId,
        })),
        label: 'Score',
      },
    ],
    '%': [
      {
        data: trend.map(item => ({
          t: item.date,
          y: pct(item.consolidation.nm.pct),
          areaId: item.areaId,
          periodId: item.periodId,
        })),
        label: 'Lost',
      },
      {
        data: trend.map(item => ({
          t: item.date,
          y: pct(item.consolidation.nc.pct),
          areaId: item.areaId,
          periodId: item.periodId,
        })),
        label: 'At-Risk',
      },

      {
        data: trend.map(item => ({
          t: item.date,
          y: pct(item.consolidation.c.pct),
          areaId: item.areaId,
          periodId: item.periodId,
        })),
        label: 'Compliant',
      },
    ],
    $: [
      {
        data: trend.map(item => ({
          t: item.date,
          y: item.consolidation.nm.val,
          areaId: item.areaId,
          periodId: item.periodId,
        })),
        label: 'Lost',
      },
      {
        data: trend.map(item => ({
          t: item.date,
          y: item.consolidation.nc.val,
          areaId: item.areaId,
          periodId: item.periodId,
        })),
        label: 'At-Risk',
      },

      {
        data: trend.map(item => ({
          t: item.date,
          y: item.consolidation.c.val,
          areaId: item.areaId,
          periodId: item.periodId,
        })),
        label: 'Compliant',
      },
    ],
  };
}

export function periodToString(period: ReportPeriod): string {
  return period === ReportPeriod.Daily ? 'Daily' : 'Monthly';
}
